import React, { useState, useEffect } from 'react';
import axios from 'axios';

const InputField = ({
  label,
  type = 'text',
  name,
  value,
  onChange,
  required = false,
  className = '',
  pattern,
  inputMode,
  readOnly = false,
  max,
  placeholder,
}) => {
  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label
          htmlFor={name}
          className="block text-xs font-semibold tracking-wide text-gray-700"
        >
          {label}
        </label>
      )}
      <input
        type={type}
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
        className={`block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition focus:border-[#520000] focus:ring-2 focus:ring-[#52000033] focus:outline-none ${className}`}
        pattern={pattern}
        inputMode={inputMode}
        max={max}
        placeholder={placeholder}
      />
    </div>
  );
};

const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear; i >= 2000; i--) {
    years.push(String(i));
  }
  return years;
};

// Parse "<Course> - Sem X"
const parseCourseAndSemester = (classSelected) => {
  if (!classSelected) return { course: '', semester: '' };

  const parts = classSelected.split(' - Sem ');
  if (parts.length === 2) {
    return {
      course: parts[0] || '',
      semester: parts[1] || '',
    };
  }

  return {
    course: classSelected,
    semester: '',
  };
};

// Build initial state from incoming row
const buildInitialFormData = (initialData) => {
  const base = {
    candidateName: '',
    candidateId: '',
    indexNo: '',
    dmcNo: '',
    school: '',
    course: '',
    semester: '',
    dob: '',
    fatherName: '',
    motherName: '',
    examMonth: '',
    resultYear: '',
    result: '',
    supwGrade: '',
    isFinalSemester: false,
    // NOTE: we are NOT editing previousYearTotal in UI anymore
    subjects: [
      {
        subjectName: '',
        subjectCode: '',
        internalMarks: '',
        externalMarks: '',
        minInternalMarks: '8',
        minExternalMarks: '32',
        maxInternalMarks: '20',
        maxExternalMarks: '80',
      },
    ],
    dynamicFields: {},
    // 🔹 default: not final semester
    isFinalSemester: false,
  };

  if (!initialData) return base;

  const rawSubjects = initialData.subjects || [];
  const subjectsToLoad =
    rawSubjects.length > 0
      ? rawSubjects.map((sub) => ({
          subjectName: sub.subjectName || sub.name || '',
          subjectCode: sub.subjectCode || sub.subject_code || '',
          internalMarks:
            sub.internalMarks ??
            sub.practicalMarks ??
            sub.practical ??
            '',
          externalMarks:
            sub.externalMarks ??
            sub.theoryMarks ??
            sub.theory ??
            '',
          minInternalMarks:
            sub.minInternalMarks ??
            sub.min_internal_marks ??
            '',
          minExternalMarks:
            sub.minExternalMarks ??
            sub.min_external_marks ??
            '',
          maxInternalMarks:
            sub.maxInternalMarks ??
            sub.max_internal_marks ??
            '20',
          maxExternalMarks:
            sub.maxExternalMarks ??
            sub.max_external_marks ??
            '80',
        }))
      : base.subjects;

  let motherName = '';
  let fatherName = '';
  if (initialData.sonOf) {
    const parts = String(initialData.sonOf).split('\n');
    motherName = parts[0] || '';
    fatherName = parts[1] || '';
  }

  const parsed = parseCourseAndSemester(initialData.classSelected);
  const resolvedCourse = initialData.course || parsed.course;
  const resolvedSemester =
    initialData.semester || parsed.semester || '';

    // 🔹 read both possible keys from backend
  const isFinalSemester =
    initialData.isFinalSemester ??
    initialData.is_final_semester ??
    false;

  return {
    ...base,
    ...initialData,
    subjects: subjectsToLoad,
    indexNo: initialData.indexNo || '',
    dmcNo: initialData.dmcNo || '',
    fatherName,
    motherName,
    course: resolvedCourse,
    semester: resolvedSemester,
    examMonth: initialData.examMonth || '',
    resultYear: initialData.resultYear || '',
    supwGrade: initialData.supwGrade || '',
    result: initialData.result || '',
    isFinalSemester:
      initialData.isFinalSemester ??
      initialData.is_final_semester ??
      false,
    dynamicFields: initialData.dynamic_fields || {},
    // 🔹 ensure our form state gets the correct boolean
    isFinalSemester: !!isFinalSemester,
  };
};

const InputForm = ({ initialData, onSubmit, isEditing }) => {
  const yearOptions = generateYearOptions();
  const [dynamicFieldDefs, setDynamicFieldDefs] = useState([]);
  const [formData, setFormData] = useState(() =>
    buildInitialFormData(initialData)
  );

  // load dynamic extra fields
  useEffect(() => {
    axios
      // .get('http://127.0.0.1:5001/api/admin/fields/')
      .get('https://peakybalvinder.pythonanywhere.com/api/admin/fields/')
      .then((res) => {
        const enabled = res.data.filter((f) => f.enabled);
        setDynamicFieldDefs(enabled);
      })
      .catch((err) => {
        console.error('Failed to load dynamic fields', err);
      });
  }, []);

  // rebuild when editing a different row
  useEffect(() => {
    setFormData(buildInitialFormData(initialData));
  }, [initialData]);

  const handleDynamicFieldChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      dynamicFields: {
        ...(prev.dynamicFields || {}),
        [key]: value,
      },
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubjectChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.subjects];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, subjects: updated };
    });
  };

  const addSubjectRow = () => {
    setFormData((prev) => ({
      ...prev,
      subjects: [
        ...prev.subjects,
        {
          subjectName: '',
          subjectCode: '',
          internalMarks: '',
          externalMarks: '',
          minInternalMarks: '',
          minExternalMarks: '',
          maxInternalMarks: '20',
          maxExternalMarks: '80',
        },
      ],
    }));
  };

  const removeSubjectRow = (index) => {
    setFormData((prev) => {
      const updated = [...prev.subjects];
      if (updated.length <= 1) return prev;
      updated.splice(index, 1);
      return { ...prev, subjects: updated };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const subjectsToSend = (formData.subjects || []).map((sub) => {
      const internal =
        sub.internalMarks === '' ? null : parseInt(sub.internalMarks, 10);
      const external =
        sub.externalMarks === '' ? null : parseInt(sub.externalMarks, 10);

      const maxInt = Number(sub.maxInternalMarks || 0) || 0;
      const maxExt = Number(sub.maxExternalMarks || 0) || 0;

      return {
        name: sub.subjectName,
        subjectCode: sub.subjectCode,
        theory: external,
        practical: internal,
        marks:
          internal == null && external == null
            ? null
            : (internal || 0) + (external || 0),
        maxMarks: maxInt + maxExt || 100,
        minInternalMarks: sub.minInternalMarks || '',
        minExternalMarks: sub.minExternalMarks || '',
        maxInternalMarks: sub.maxInternalMarks || '20',
        maxExternalMarks: sub.maxExternalMarks || '80',
      };
    });

    const motherName = formData.motherName || '';
    const fatherName = formData.fatherName || '';

    const apiSonOf =
      motherName || fatherName
        ? [motherName, fatherName].filter(Boolean).join('\n')
        : '';

    const classSelectedString =
      formData.course && formData.semester
        ? `${formData.course} - Sem ${formData.semester}`
        : formData.course || '';

    const {
      fatherName: _f,
      motherName: _m,
      course,
      semester,
      subjects,
      dynamicFields,
      ...rest
    } = formData;

    const dataToSend = {
      ...rest,
      semester: formData.semester || '',
      subjects: subjectsToSend,
      sonOf: apiSonOf,
      classSelected: classSelectedString,
      dynamic_fields: dynamicFields || {},
      is_final_semester: !!formData.isFinalSemester,
      // 🔴 NO previous_year_total here – backend will compute from previous semesters
    };

    onSubmit(dataToSend);
  };

  const isIndexLocked = !!(initialData && initialData.indexNo);

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Candidate Details */}
      <section className="rounded-2xl border border-gray-200 bg-gray-50/70 px-4 py-5 md:px-6 md:py-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-base md:text-lg font-bold text-gray-900">
              Candidate Details
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Basic information appearing on the marksheet
            </p>
          </div>

          <div className="flex items-center gap-2">
            <input
              id="isFinalSemester"
              name="isFinalSemester"
              type="checkbox"
              checked={!!formData.isFinalSemester}
              onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                isFinalSemester: e.target.checked,            // 🔹 keep boolean
              }))
            }
              className="h-4 w-4 rounded border-gray-300 text-[#520000] focus:ring-[#520000]"
            />
            <label
              htmlFor="isFinalSemester"
              className="text-xs font-semibold text-gray-700"
            >
              Is this Final Semester?
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <InputField
            label="Candidate Name"
            name="candidateName"
            value={formData.candidateName}
            onChange={handleChange}
          />

          <InputField
            label="University Roll No"
            type="text"
            name="candidateId"
            value={formData.candidateId}
            onChange={handleChange}
            readOnly={isEditing}
            className={isEditing ? 'bg-gray-100 cursor-not-allowed' : ''}
          />

          <InputField
            label="DMC No."
            type="text"
            name="dmcNo"
            value={formData.dmcNo}
            onChange={handleChange}
          />

          <InputField
            label="Index / Reg. No"
            type="text"
            name="indexNo"
            value={formData.indexNo}
            onChange={handleChange}
            readOnly={isIndexLocked}
            className={isIndexLocked ? 'bg-gray-100 cursor-not-allowed' : ''}
          />

          <InputField
            label="Course"
            name="course"
            type="text"
            value={formData.course}
            onChange={handleChange}
            placeholder="e.g. B.Com, BCA, B.Sc"
          />

          {/* Semester */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="semester"
              className="block text-xs font-semibold tracking-wide text-gray-700"
            >
              Semester
            </label>
            <select
              id="semester"
              name="semester"
              value={formData.semester}
              onChange={handleChange}
              className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition focus:border-[#520000] focus:ring-2 focus:ring-[#52000033] focus:outline-none"
            >
              <option value="">Select Semester</option>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((sem) => (
                <option key={sem} value={sem}>
                  Semester {sem}
                </option>
              ))}
            </select>
          </div>

          {/* Parents */}
          <InputField
            label="Father's Name"
            name="fatherName"
            value={formData.fatherName}
            onChange={handleChange}
          />

          <InputField
            label="Mother's Name"
            name="motherName"
            value={formData.motherName}
            onChange={handleChange}
          />

          {/* Exam Month */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="examMonth"
              className="block text-xs font-semibold tracking-wide text-gray-700"
            >
              Exam Month
            </label>
            <select
              id="examMonth"
              name="examMonth"
              value={formData.examMonth}
              onChange={handleChange}
              className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition focus:border-[#520000] focus:ring-2 focus:ring-[#52000033] focus:outline-none"
            >
              <option value="">Select Month</option>
              {[
                'January',
                'February',
                'March',
                'April',
                'May',
                'June',
                'July',
                'August',
                'September',
                'October',
                'November',
                'December',
              ].map((month) => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </select>
          </div>

          {/* Exam Year */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="resultYear"
              className="block text-xs font-semibold tracking-wide text-gray-700"
            >
              Exam Year
            </label>
            <select
              id="resultYear"
              name="resultYear"
              value={formData.resultYear}
              onChange={handleChange}
              className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition focus:border-[#520000] focus:ring-2 focus:ring-[#52000033] focus:outline-none"
            >
              <option value="">Select Year</option>
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {dynamicFieldDefs.map((field) => (
            <InputField
              key={field.key}
              label={field.label}
              name={field.key}
              type={field.type === 'dropdown' ? 'text' : field.type}
              value={formData.dynamicFields?.[field.key] || ''}
              onChange={(e) =>
                handleDynamicFieldChange(field.key, e.target.value)
              }
              required={field.required}
              placeholder={
                field.type === 'dropdown'
                  ? (field.options || []).join(', ')
                  : ''
              }
            />
          ))}
        </div>
      </section>

      {/* Subject Marks – compact, no Grade */}
      <section className="rounded-2xl border border-gray-200 bg-white px-4 py-5 md:px-6 md:py-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-base md:text-lg font-bold text-gray-900">
              Subject-wise Marks
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Internal / External + pass marks (per subject)
            </p>
          </div>
          <span className="material-symbols-outlined text-[#520000] opacity-70 text-xl md:text-2xl">
            menu_book
          </span>
        </div>

        {formData.subjects.map((subject, index) => (
          <div
            key={index}
            className="mb-3 flex flex-col gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 md:px-4 md:py-3"
          >
            {/* Top row: heading + add/remove */}
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-600">
                Subject #{index + 1}
              </span>
              <div className="flex gap-2">
                {formData.subjects.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeSubjectRow(index)}
                    className="inline-flex items-center gap-1 rounded-full bg-red-500 px-3 py-1 text-[11px] font-semibold text-white shadow-sm hover:bg-red-600"
                  >
                    <span className="material-symbols-outlined text-[14px]">
                      remove
                    </span>
                    Remove
                  </button>
                )}
                {index === formData.subjects.length - 1 && (
                  <button
                    type="button"
                    onClick={addSubjectRow}
                    className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1 text-[11px] font-semibold text-white shadow-sm hover:bg-emerald-700"
                  >
                    <span className="material-symbols-outlined text-[14px]">
                      add
                    </span>
                    Add Subject
                  </button>
                )}
              </div>
            </div>

            {/* Row 1: subject + code */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <InputField
                label="Subject Name"
                name={`subjectName-${index}`}
                value={subject.subjectName}
                onChange={(e) =>
                  handleSubjectChange(index, 'subjectName', e.target.value)
                }
                required
              />
              <InputField
                label="Subject Code"
                name={`subjectCode-${index}`}
                value={subject.subjectCode}
                onChange={(e) =>
                  handleSubjectChange(index, 'subjectCode', e.target.value)
                }
              />
              {/* leave 3rd col empty for nicer wrapping, or use later if needed */}
            </div>

            {/* Row 2: 3 compact groups */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Marks Obtained */}
              <div>
                <p className="text-[11px] font-semibold text-gray-600 mb-1">
                  Marks Obtained
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <InputField
                    label="Internal"
                    type="number"
                    name={`internalMarks-${index}`}
                    value={subject.internalMarks}
                    onChange={(e) =>
                      handleSubjectChange(
                        index,
                        'internalMarks',
                        e.target.value
                      )
                    }
                    placeholder="18"
                  />
                  <InputField
                    label="External"
                    type="number"
                    name={`externalMarks-${index}`}
                    value={subject.externalMarks}
                    onChange={(e) =>
                      handleSubjectChange(
                        index,
                        'externalMarks',
                        e.target.value
                      )
                    }
                    placeholder="72"
                  />
                </div>
              </div>

              {/* Min Pass */}
              <div>
                <p className="text-[11px] font-semibold text-gray-600 mb-1">
                  Min Pass Marks
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <InputField
                    label="Internal"
                    type="number"
                    name={`minInternalMarks-${index}`}
                    value={subject.minInternalMarks}
                    onChange={(e) =>
                      handleSubjectChange(
                        index,
                        'minInternalMarks',
                        e.target.value
                      )
                    }
                    placeholder="8"
                  />
                  <InputField
                    label="External"
                    type="number"
                    name={`minExternalMarks-${index}`}
                    value={subject.minExternalMarks}
                    onChange={(e) =>
                      handleSubjectChange(
                        index,
                        'minExternalMarks',
                        e.target.value
                      )
                    }
                    placeholder="32"
                  />
                </div>
              </div>

              {/* Max Marks + total */}
              <div>
                <p className="text-[11px] font-semibold text-gray-600 mb-1">
                  Max Marks
                </p>
                <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                  <InputField
                    label="Internal"
                    type="number"
                    name={`maxInternalMarks-${index}`}
                    value={subject.maxInternalMarks}
                    onChange={(e) =>
                      handleSubjectChange(
                        index,
                        'maxInternalMarks',
                        e.target.value
                      )
                    } 
                    placeholder="20"
                  />
                  <InputField
                    label="External"
                    type="number"
                    name={`maxExternalMarks-${index}`}
                    value={subject.maxExternalMarks}
                    onChange={(e) =>
                      handleSubjectChange(
                        index,
                        'maxExternalMarks',
                        e.target.value
                      )
                    }
                    placeholder="80"
                  />
                  <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-semibold text-gray-600">
                      Total
                    </span>
                    <div className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs text-gray-800 text-center">
                      {(Number(subject.maxInternalMarks || 0) || 0) +
                        (Number(subject.maxExternalMarks || 0) || 0) ||
                        100}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {formData.subjects.length === 0 && (
          <div className="flex justify-center mt-4">
            <button
              type="button"
              onClick={addSubjectRow}
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-6 py-2 text-sm font-semibold text-white shadow-md hover:bg-emerald-700"
            >
              <span className="material-symbols-outlined text-[18px]">
                add
              </span>
              Add Subject
            </button>
          </div>
        )}
      </section>

      <div className="flex justify-center pt-2">
        <button
          type="submit"
          className="inline-flex items-center gap-2 rounded-full bg-[#520000] px-10 py-3 text-sm md:text-base font-semibold text-white shadow-md transition hover:bg-[#6a1717] hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#52000055]"
        >
          <span className="material-symbols-outlined text-base md:text-lg">
            save
          </span>
          {isEditing ? 'Save Changes' : 'Save Candidate'}
        </button>
      </div>
    </form>
  );
};

export default InputForm;